import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UsersManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cohortFilter, setCohortFilter] = useState('');
  const [scholarshipFilter, setScholarshipFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const { getToken } = useAdminAuth();

  // Bulk Actions State
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkScholarshipPercentage, setBulkScholarshipPercentage] = useState('');
  const [bulkCohort, setBulkCohort] = useState('');
  const [bulkNotificationMessage, setBulkNotificationMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Advanced Filters State
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [registrationDateFrom, setRegistrationDateFrom] = useState('');
  const [registrationDateTo, setRegistrationDateTo] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [statusFilter, cohortFilter]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('q', searchQuery);
      if (cohortFilter) params.append('cohort', cohortFilter);

      const response = await axios.get(
        `${BACKEND_URL}/api/admin/users?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/users/${userId}/enrollment`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      fetchUsers();
      alert('Status actualizado correctamente');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error al actualizar el status');
    }
  };

  // Bulk Actions Functions
  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredAndSortedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredAndSortedUsers.map(user => user.id));
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) {
      alert('Por favor selecciona una acción y al menos un usuario');
      return;
    }

    setIsProcessing(true);

    try {
      if (bulkAction === 'scholarship') {
        if (!bulkScholarshipPercentage) {
          alert('Por favor ingresa el porcentaje de beca');
          setIsProcessing(false);
          return;
        }

        // Apply scholarship to selected users
        await Promise.all(
          selectedUsers.map(userId =>
            axios.post(
              `${BACKEND_URL}/api/admin/users/${userId}/scholarship`,
              { percentage: parseInt(bulkScholarshipPercentage) },
              { headers: { Authorization: `Bearer ${getToken()}` } }
            )
          )
        );
        alert(`Beca del ${bulkScholarshipPercentage}% aplicada a ${selectedUsers.length} usuarios`);
      } else if (bulkAction === 'cohort') {
        if (!bulkCohort) {
          alert('Por favor selecciona un cohorte');
          setIsProcessing(false);
          return;
        }

        // Move users to cohort
        await Promise.all(
          selectedUsers.map(userId =>
            axios.patch(
              `${BACKEND_URL}/api/admin/users/${userId}`,
              { cohort: bulkCohort },
              { headers: { Authorization: `Bearer ${getToken()}` } }
            )
          )
        );
        alert(`${selectedUsers.length} usuarios movidos al cohorte ${bulkCohort}`);
      } else if (bulkAction === 'notification') {
        if (!bulkNotificationMessage) {
          alert('Por favor escribe un mensaje');
          setIsProcessing(false);
          return;
        }

        // Send notification to selected users
        await Promise.all(
          selectedUsers.map(userId =>
            axios.post(
              `${BACKEND_URL}/api/admin/users/${userId}/notify`,
              { message: bulkNotificationMessage },
              { headers: { Authorization: `Bearer ${getToken()}` } }
            )
          )
        );
        alert(`Notificación enviada a ${selectedUsers.length} usuarios`);
      }

      // Reset and refresh
      setSelectedUsers([]);
      setShowBulkModal(false);
      setBulkAction('');
      setBulkScholarshipPercentage('');
      setBulkCohort('');
      setBulkNotificationMessage('');
      fetchUsers();
    } catch (error) {
      console.error('Error processing bulk action:', error);
      alert('Error al procesar la acción masiva');
    } finally {
      setIsProcessing(false);
    }
  };

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = [...users];

    // Apply advanced filters
    if (scholarshipFilter === 'yes') {
      filtered = filtered.filter(u => u.enrollment?.scholarship_percentage > 0);
    } else if (scholarshipFilter === 'no') {
      filtered = filtered.filter(u => !u.enrollment?.scholarship_percentage || u.enrollment?.scholarship_percentage === 0);
    }

    if (paymentStatusFilter === 'paid') {
      filtered = filtered.filter(u => (u.enrollment?.balance || 0) === 0);
    } else if (paymentStatusFilter === 'pending') {
      filtered = filtered.filter(u => (u.enrollment?.balance || 0) > 0);
    }

    if (registrationDateFrom) {
      const fromDate = new Date(registrationDateFrom);
      filtered = filtered.filter(u => new Date(u.created_at) >= fromDate);
    }

    if (registrationDateTo) {
      const toDate = new Date(registrationDateTo);
      filtered = filtered.filter(u => new Date(u.created_at) <= toDate);
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.full_name || '';
          bValue = b.full_name || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'status':
          aValue = a.enrollment?.status || '';
          bValue = b.enrollment?.status || '';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [users, scholarshipFilter, paymentStatusFilter, registrationDateFrom, registrationDateTo, sortBy, sortOrder]);

  const getStatusBadge = (status) => {
    const styles = {
      preview: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      active: 'bg-green-500/10 text-green-400 border-green-500/30',
      suspended: 'bg-red-500/10 text-red-400 border-red-500/30',
    };
    return styles[status] || styles.preview;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Gestión de Usuarios</h1>
          <p className="text-gray-400 mt-1">Administra los usuarios registrados en la plataforma</p>
        </div>
        
        {/* Bulk Actions Button */}
        {selectedUsers.length > 0 && (
          <button
            onClick={() => setShowBulkModal(true)}
            className="px-4 py-2 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors"
          >
            Acciones Masivas ({selectedUsers.length})
          </button>
        )}
      </div>

      {/* Basic Filters */}
      <div className="bg-[#0A0A0A] rounded-lg p-6 border border-[#1a4d5c]/30">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="flex-1 px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]"
            />
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4E4B7] text-black font-medium rounded-lg hover:from-[#F4E4B7] hover:to-[#D4AF37] transition-colors"
            >
              Buscar
            </button>
          </form>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="">Todos los status</option>
            <option value="preview">Preview</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>

          <select
            value={cohortFilter}
            onChange={(e) => setCohortFilter(e.target.value)}
            className="px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
          >
            <option value="">Todos los cohortes</option>
            <option value="2024-Q1">2024-Q1</option>
            <option value="2024-Q2">2024-Q2</option>
            <option value="2024-Q3">2024-Q3</option>
            <option value="2024-Q4">2024-Q4</option>
          </select>

          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white hover:border-[#D4AF37] transition-colors whitespace-nowrap"
          >
            {showAdvancedFilters ? '⬆️' : '⬇️'} Filtros Avanzados
          </button>
        </div>

        {/* Advanced Filters */}
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-[#1a4d5c]/30">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Beca</label>
                <select
                  value={scholarshipFilter}
                  onChange={(e) => setScholarshipFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                >
                  <option value="">Todos</option>
                  <option value="yes">Con beca</option>
                  <option value="no">Sin beca</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Estado de Pago</label>
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                >
                  <option value="">Todos</option>
                  <option value="paid">Pagado</option>
                  <option value="pending">Pendiente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Fecha Desde</label>
                <input
                  type="date"
                  value={registrationDateFrom}
                  onChange={(e) => setRegistrationDateFrom(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Fecha Hasta</label>
                <input
                  type="date"
                  value={registrationDateTo}
                  onChange={(e) => setRegistrationDateTo(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  setScholarshipFilter('');
                  setPaymentStatusFilter('');
                  setRegistrationDateFrom('');
                  setRegistrationDateTo('');
                }}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sorting */}
      <div className="flex items-center gap-4 text-sm text-gray-400">
        <span>Ordenar por:</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-1 bg-black/50 border border-[#1a4d5c]/50 rounded text-white focus:outline-none focus:border-[#D4AF37]"
        >
          <option value="created_at">Fecha de Registro</option>
          <option value="name">Nombre</option>
          <option value="email">Email</option>
          <option value="status">Status</option>
        </select>
        <button
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          className="px-3 py-1 bg-black/50 border border-[#1a4d5c]/50 rounded text-white hover:border-[#D4AF37] transition-colors"
        >
          {sortOrder === 'asc' ? '↑ Ascendente' : '↓ Descendente'}
        </button>
        <span className="ml-auto">
          Mostrando {filteredAndSortedUsers.length} de {users.length} usuarios
        </span>
      </div>

      {/* Users Table */}
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1a4d5c]/30 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div>
              <p className="mt-4 text-gray-400">Cargando usuarios...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-black/50">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredAndSortedUsers.length && filteredAndSortedUsers.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-600 text-[#D4AF37] focus:ring-[#D4AF37]"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Beca
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Cohorte
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Fecha Registro
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredAndSortedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleSelectUser(user.id)}
                        className="w-4 h-4 rounded border-gray-600 text-[#D4AF37] focus:ring-[#D4AF37]"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4E4B7] flex items-center justify-center text-black font-bold">
                          {user.full_name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{user.full_name}</div>
                          <div className="text-xs text-gray-400">ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                          user.enrollment?.status
                        )}`}
                      >
                        {user.enrollment?.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {user.enrollment?.scholarship_percentage ? `${user.enrollment.scholarship_percentage}%` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {user.cohort || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {new Date(user.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="text-[#D4AF37] hover:text-[#F4E4B7] font-medium"
                        >
                          Ver detalle
                        </button>
                        {user.enrollment?.status === 'preview' && (
                          <button
                            onClick={() => handleStatusChange(user.id, 'active')}
                            className="text-green-500 hover:text-green-400 font-medium"
                          >
                            Activar
                          </button>
                        )}
                        {user.enrollment?.status === 'active' && (
                          <button
                            onClick={() => handleStatusChange(user.id, 'suspended')}
                            className="text-red-500 hover:text-red-400 font-medium"
                          >
                            Suspender
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredAndSortedUsers.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-400">No hay usuarios</h3>
                <p className="mt-1 text-sm text-gray-500">No se encontraron usuarios con los filtros aplicados.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] rounded-lg max-w-2xl w-full border border-[#1a4d5c]/30 p-6">
            <h3 className="text-2xl font-bold text-white mb-4">
              Acciones Masivas ({selectedUsers.length} usuarios seleccionados)
            </h3>

            <div className="space-y-4">
              {/* Select Action */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Selecciona una acción</label>
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                >
                  <option value="">-- Selecciona --</option>
                  <option value="scholarship">Aplicar Beca</option>
                  <option value="cohort">Mover a Cohorte</option>
                  <option value="notification">Enviar Notificación</option>
                </select>
              </div>

              {/* Scholarship Percentage */}
              {bulkAction === 'scholarship' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Porcentaje de Beca (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={bulkScholarshipPercentage}
                    onChange={(e) => setBulkScholarshipPercentage(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Ej: 50"
                  />
                </div>
              )}

              {/* Cohort Selection */}
              {bulkAction === 'cohort' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Cohorte Destino</label>
                  <select
                    value={bulkCohort}
                    onChange={(e) => setBulkCohort(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                  >
                    <option value="">-- Selecciona --</option>
                    <option value="2024-Q1">2024-Q1</option>
                    <option value="2024-Q2">2024-Q2</option>
                    <option value="2024-Q3">2024-Q3</option>
                    <option value="2024-Q4">2024-Q4</option>
                    <option value="2025-Q1">2025-Q1</option>
                  </select>
                </div>
              )}

              {/* Notification Message */}
              {bulkAction === 'notification' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Mensaje de Notificación</label>
                  <textarea
                    value={bulkNotificationMessage}
                    onChange={(e) => setBulkNotificationMessage(e.target.value)}
                    rows="4"
                    className="w-full px-4 py-2 bg-black/50 border border-[#1a4d5c]/50 rounded-lg text-white focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Escribe el mensaje que se enviará a todos los usuarios seleccionados..."
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleBulkAction}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4E4B7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Procesando...' : 'Ejecutar Acción'}
              </button>
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkAction('');
                  setBulkScholarshipPercentage('');
                  setBulkCohort('');
                  setBulkNotificationMessage('');
                }}
                disabled={isProcessing}
                className="px-6 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
